import { CircularProgress } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { FieldTitle, Record, useDataProvider } from "ra-core";
import { InputHelperText } from "ra-ui-materialui";
import TextField from "@material-ui/core/TextField";
import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { Ref, useState } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { HookFormInputProps } from "./HookFormInputProps";
import { debounce } from "lodash";
import { useSearchFields } from "../../../../../common/hooks/useSearchFields";

declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

interface Props<TRecord> extends HookFormInputProps<TRecord> {
  resource: string;
  margin?: "none" | "dense" | "normal";
  variant?: "standard" | "outlined" | "filled";
  perPage?: number;
  sortField?: string;
  optionText: string;
  optionValue?: string;
  renderOption?: (option: Record, state: object) => ReactNode;
  filter?: any;
}

const HookFormAutocompleteInputComponent = <TRecord extends FieldValues>(
  {
    control,
    name,
    defaultValue,
    rules,
    variant = "outlined",
    margin = "dense",
    resource,
    perPage = 25,
    label,
    optionText,
    sortField = "id",
    renderOption,
    optionValue = "id",
    filter,
  }: Props<TRecord>,
  ref: Ref<HTMLElement>
) => {
  useSearchFields({ resource }, [optionText]);
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState<boolean>(false);
  const [inputText, setInputText] = useState<string>("");
  const [data, setData] = useState<Record[]>([]);

  const getData = useCallback(
    (nextValue: string) => {
      setLoading(true);
      dataProvider
        .getList(resource, {
          pagination: { page: 1, perPage },
          sort: { field: sortField, order: "asc" },
          filter: { ...filter, q: nextValue },
        })
        .then((value) => setData(value.data))
        .catch((e) => console.error(e))
        .finally(() => setLoading(false));
    },
    [dataProvider, perPage, sortField, resource, filter]
  );

  const debouncedRefresh = useMemo(
    () =>
      debounce(async (nextValue) => {
        getData(nextValue);
      }, 500),
    [getData]
  );

  useEffect(() => {
    getData("");
  }, [getData]);

  let renderOptions: {
    renderOption?: (option: Record, state: object) => ReactNode;
    getOptionLabel?: (option: Record) => string;
  } = {
    renderOption: undefined,
    getOptionLabel: (option) => {
      return option ? option[optionText] ?? "" : "";
    },
  };
  if (renderOption) {
    renderOptions.renderOption = renderOption;
  } else {
    renderOptions.getOptionLabel = (option) => {
      return option ? option[optionText] ?? "" : "";
    };
  }

  return (
    <Controller
      render={(props) => (
        <Autocomplete
          {...props.field}
          blurOnSelect
          autoComplete
          options={data}
          loading={loading}
          getOptionSelected={(option, value) =>
            option?.[optionValue] === value?.[optionValue]
          }
          filterOptions={(options) => options}
          ref={ref}
          inputValue={inputText}
          {...renderOptions}
          onInputChange={(_, value) => {
            setInputText(value);
            debouncedRefresh(value);
          }}
          renderInput={(params) => (
            <TextField
              inputRef={props.field.ref}
              {...params}
              label={
                <FieldTitle
                  label={label}
                  source={name}
                  isRequired={!!rules?.required}
                />
              }
              error={props.fieldState.isTouched && !!props.fieldState.error}
              variant={variant}
              margin={margin}
              helperText={
                <InputHelperText
                  touched={props.fieldState.isTouched}
                  error={(props.fieldState.error as any)?.message}
                  helperText={(props.fieldState.error as any)?.message ?? " "}
                />
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          onChange={(_, data) => props.field.onChange(data?.[optionValue])}
          value={
            data.find((x) => x[optionValue] === props.field.value) ??
            defaultValue ??
            null
          }
        />
      )}
      name={name}
      control={control}
      defaultValue={defaultValue ?? null}
      rules={rules}
    />
  );
};

export const HookFormAutocompleteInput = React.forwardRef(
  HookFormAutocompleteInputComponent
);
